/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import {
	Box,
	Button,
	Checkbox,
	Flex,
	FormControl,
	HStack,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	SimpleGrid,
	Text,
	useToast,
} from '@chakra-ui/react';
import { Field, Form, Formik } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import c from '../../../constant';

const Q_CHECK = gql`
	query get($companyId: String!) {
		checkCompanyDisablementConstraint(companyId: $companyId) {
			company {
				id
				name
				nameKhmer
				logo
				active
			}
			directAgentConstraints
			saleStaffConstraints
		}
	}
`;

const M_STATUS = gql`
	mutation update($companyId: String!, $status: Boolean!) {
		updateCompanyStatusWithoutConstraintByCompanyId(companyId: $companyId, status: $status)
	}
`;

const M_STATUS_CONSTRAINT = gql`
	mutation update(
		$companyId: String!
		$directAgentReason: String
		$directAgentInternalReason: String
		$saleStaffReason: String
		$saleStaffInternalReason: String
	) {
		disableCompanyWithConstraintByCompanyId(
			companyId: $companyId
			directAgentReason: $directAgentReason
			directAgentInternalReason: $directAgentInternalReason
			saleStaffReason: $saleStaffReason
			saleStaffInternalReason: $saleStaffInternalReason
		)
	}
`;

const validationSchema = Yup.object().shape({
	directAgentConstraints: Yup.number(),
	saleStaffConstraints: Yup.number(),
	directAgentReason: Yup.mixed().when('directAgentConstraints', {
		is: (directAgentConstraints) => directAgentConstraints > 0,
		then: Yup.string().required('required'),
		otherwise: Yup.string().nullable(),
	}),
	directAgentInternalReason: Yup.mixed().when('directAgentConstraints', {
		is: (directAgentConstraints) => directAgentConstraints > 0,
		then: Yup.string().required('required'),
		otherwise: Yup.string().nullable(),
	}),
	saleStaffReason: Yup.mixed().when('saleStaffConstraints', {
		is: (saleStaffConstraints) => saleStaffConstraints > 0,
		then: Yup.string().required('required'),
		otherwise: Yup.string().nullable(),
	}),
	saleStaffInternalReason: Yup.mixed().when('saleStaffConstraints', {
		is: (saleStaffConstraints) => saleStaffConstraints > 0,
		then: Yup.string().required('required'),
		otherwise: Yup.string().nullable(),
	}),
	agreement: Yup.mixed().when(['directAgentConstraints', 'saleStaffConstraints'], {
		is: (directAgentConstraints, saleStaffConstraints) => directAgentConstraints > 0 || saleStaffConstraints > 0,
		then: Yup.boolean().test('agreement', 'you must agree first', (value) => value === true),
		otherwise: Yup.boolean(),
	}),
});

export default function CompanyDisablementModal({ company = null, onClose = () => null, onDataChange = () => null }) {
	const toast = useToast();
	const { t } = useTranslation();
	const [loadCheck, { data }] = useLazyQuery(Q_CHECK);
	const [updateCompanyStatus, { loading: loadingStatus }] = useMutation(M_STATUS);
	const [disableCompanyStatus, { loading: loadingStatusContraint }] = useMutation(M_STATUS_CONSTRAINT);

	const load = () => {
		if (company?.id) {
			loadCheck({ variables: { companyId: company?.id } });
		}
	};

	useEffect(load, [company?.id]);

	const InputField = ({ name = null, placeholder = null, size = 'sm' | 'md' | 'lg' }) => {
		return (
			<Field name={name}>
				{({ field, meta }) => (
					<FormControl id={name}>
						<Input
							{...field}
							value={field?.value ?? ''}
							id={name}
							type="text"
							placeholder={placeholder ? placeholder : ''}
							size={size}
							errorBorderColor="red.300"
							isInvalid={meta.touched && meta.error}
							onWheelCapture={(e) => e.target.blur()}
						/>
					</FormControl>
				)}
			</Field>
		);
	};

	return (
		<>
			{data?.checkCompanyDisablementConstraint && (
				<>
					{data?.checkCompanyDisablementConstraint?.directAgentConstraints > 0 ||
					data?.checkCompanyDisablementConstraint?.saleStaffConstraints > 0 ? (
						<>
							<Formik
								validationSchema={validationSchema}
								initialValues={{
									directAgentConstraints: data?.checkCompanyDisablementConstraint?.directAgentConstraints,
									saleStaffConstraints: data?.checkCompanyDisablementConstraint?.saleStaffConstraints,
									directAgentReason: '',
									directAgentInternalReason: '',
									saleStaffReason: '',
									saleStaffInternalReason: '',
									agreement: false,
								}}
								onSubmit={(values) => {
									disableCompanyStatus({
										variables: {
											companyId: company?.id,
											directAgentReason: values?.directAgentReason,
											directAgentInternalReason: values?.directAgentInternalReason,
											saleStaffReason: values?.saleStaffReason,
											saleStaffInternalReason: values?.saleStaffInternalReason,
										},
									})
										.then((result) => {
											onClose();
											onDataChange();
											toast({
												title: t('Company updated successfully'),
												status: 'success',
												isClosable: true,
												position: 'top-right',
											});
										})
										.catch((error) => {
											toast({
												title: t(error?.message ? error?.message : 'something went wrong'),
												status: 'error',
												isClosable: true,
												position: 'bottom-right',
											});
										});
								}}
							>
								{({ errors, ...props }) => {
									return (
										<Modal
											size="xl"
											borderRadius={c.borderRadius}
											isOpen={company?.id ? true : false}
											onClose={onClose}
											closeOnEsc={loadingStatusContraint ? false : true}
											closeOnOverlayClick={loadingStatusContraint ? false : true}
										>
											<ModalOverlay />
											<ModalContent borderRadius={c.borderRadius}>
												<Form>
													<ModalHeader>{t('Deactivate company')}</ModalHeader>
													<ModalCloseButton isDisabled={loadingStatusContraint} />
													<ModalBody>
														<Text fontSize="md" fontWeight="normal" borderColor="gray.600">
															{t('Please reviewing the following constraints before proceeding to disable company')}
														</Text>
														<SimpleGrid width="100%" columns={1} spacingY={2}>
															{data?.checkCompanyDisablementConstraint?.directAgentConstraints > 0 && (
																<SimpleGrid
																	columns={1}
																	spacing={2}
																	borderWidth={2}
																	borderStyle="dotted"
																	borderColor={
																		errors?.directAgentReason || errors?.directAgentInternalReason ? 'red.300' : 'gray.600'
																	}
																	mt={2}
																	mb={2}
																	p={4}
																>
																	<Box>
																		<Text fontSize="md" fontWeight="normal" color="gray.600">
																			<span style={{ fontWeight: 'bolder', fontSize: '20px' }}>
																				{data?.checkCompanyDisablementConstraint?.directAgentConstraints}
																				&nbsp;
																			</span>
																			{t(
																				'direct agent(s) will be dismissed from the company. Please review it at company profile at',
																			)}
																		</Text>
																		<Text fontSize="md" fontWeight="normal" color="gray.600">
																			<span style={{ fontWeight: 'bold' }}>{`${t('Company Detail')}`}</span>
																			<span style={{ fontWeight: 'bold' }}>{` > ${t('Distribution System')}`}</span>
																			<span style={{ fontWeight: 'bold' }}>{` > ${t('Agent')}`}</span>
																			<span style={{ fontWeight: 'bold' }}>{` > ${t('Agent')}`}</span>
																		</Text>
																		<Text fontSize="md" fontWeight="normal" color="gray.600">
																			{t('or proceeding to disable company')}
																		</Text>
																	</Box>
																	<InputField {...props} size="md" placeholder={t('Reason')} name="directAgentReason" />
																	<InputField
																		{...props}
																		size="md"
																		placeholder={t('Internal reason')}
																		name="directAgentInternalReason"
																	/>
																</SimpleGrid>
															)}
															{data?.checkCompanyDisablementConstraint?.saleStaffConstraints > 0 && (
																<SimpleGrid
																	columns={1}
																	spacing={2}
																	borderWidth={2}
																	borderStyle="dotted"
																	borderColor={
																		errors?.saleStaffReason || errors?.saleStaffInternalReason ? 'red.300' : 'gray.600'
																	}
																	mt={2}
																	mb={2}
																	p={4}
																>
																	<Box>
																		<Text fontSize="md" fontWeight="normal" color="gray.600">
																			<span style={{ fontWeight: 'bolder', fontSize: '20px' }}>
																				{data?.checkCompanyDisablementConstraint?.saleStaffConstraints}
																				&nbsp;
																			</span>
																			{t(
																				'sale staffs will be dismissed from the company. Please review it at company profile at',
																			)}
																		</Text>
																		<Text fontSize="md" fontWeight="normal" color="gray.600">
																			<span style={{ fontWeight: 'bold' }}>{`${t('Company Detail')}`}</span>
																			<span style={{ fontWeight: 'bold' }}>{` > ${t('Distribution System')}`}</span>
																			<span style={{ fontWeight: 'bold' }}>{` > ${t('Sale Staff')}`}</span>
																		</Text>
																		<Text fontSize="md" fontWeight="normal" color="gray.600">
																			{t('or proceeding to disable company')}
																		</Text>
																	</Box>
																	<InputField {...props} size="md" placeholder={t('Reason')} name="saleStaffReason" />
																	<InputField
																		{...props}
																		size="md"
																		placeholder={t('Internal reason')}
																		name="saleStaffInternalReason"
																	/>
																</SimpleGrid>
															)}
															<Field name="agreement">
																{({ field, meta, form }) => {
																	return (
																		<Box mb="4">
																			<FormControl id="agreement">
																				<Checkbox
																					{...field}
																					isChecked={field?.value}
																					onChange={(e) => form.setFieldValue('agreement', e?.target?.checked)}
																				>
																					<Text color={meta.touched && meta.error ? 'red.400' : 'gray.600'}>
																						{t('Agree on dismission and proceeding to disable company')}
																					</Text>
																				</Checkbox>
																			</FormControl>
																		</Box>
																	);
																}}
															</Field>
														</SimpleGrid>
													</ModalBody>
													<ModalFooter>
														<Flex justifyContent="flex-end" alignItems="center">
															<HStack>
																<Button variant="solid" size="md" colorScheme="gray" onMouseDown={onClose}>
																	{t('Cancel')}
																</Button>
																<Button variant="solid" size="md" colorScheme="red" type="submit">
																	{t('Okay')}
																</Button>
															</HStack>
														</Flex>
													</ModalFooter>
												</Form>
											</ModalContent>
										</Modal>
									);
								}}
							</Formik>
						</>
					) : (
						<Modal
							borderRadius={c.borderRadius}
							isOpen={company?.id ? true : false}
							onClose={onClose}
							closeOnEsc={loadingStatus ? false : true}
							closeOnOverlayClick={loadingStatus ? false : true}
						>
							<ModalOverlay />
							<ModalContent borderRadius={c.borderRadius}>
								<ModalHeader>{t(company?.active ? 'Deactivate company' : 'Activate company')}</ModalHeader>
								<ModalCloseButton isDisabled={loadingStatus} />
								<ModalBody>
									<Text fontSize="md" fontWeight="normal" borderColor="gray.600">
										{t(company?.active ? 'Are you sure to deactivate this company?' : 'Are you sure to activate this company?')}
									</Text>
									<Flex justifyContent="flex-end" alignItems="center" mt={4} mb={2}>
										<HStack>
											<Button variant="solid" size="md" colorScheme="gray" onMouseDown={onClose}>
												{t('Cancel')}
											</Button>
											<Button
												variant="solid"
												size="md"
												colorScheme={company?.active ? 'red' : 'green'}
												onMouseDown={() =>
													updateCompanyStatus({
														variables: {
															companyId: company?.id,
															status: company?.active ? false : true,
														},
													})
														.then((result) => {
															onClose();
															onDataChange();
															toast({
																title: t('Company updated successfully'),
																status: 'success',
																isClosable: true,
																position: 'top-right',
															});
														})
														.catch((error) => {
															toast({
																title: t(error?.message ? error?.message : 'something went wrong'),
																status: 'error',
																isClosable: true,
																position: 'bottom-right',
															});
														})
												}
											>
												{t('Okay')}
											</Button>
										</HStack>
									</Flex>
								</ModalBody>
							</ModalContent>
						</Modal>
					)}
				</>
			)}
		</>
	);
}
